import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import {
  BadgeCheck, Building2, Sparkles, ChevronRight, MapPin, Award,
} from "lucide-react";
import { getBrokerProfile, type BrokerStats } from "@/lib/brokers.functions";

type Props = {
  brokerSlug: string;
  fallbackName: string;
  fallbackInitials: string;
  fallbackAvatar?: string | null;
  onContact: () => void;
  onSchedule: () => void;
};

const BAND_LABEL: Record<NonNullable<BrokerStats["avg_quality_band"]>, string> = {
  excepcional: "Excepcional",
  raro: "Raro",
  solido: "Sólido",
  regular: "Regular",
};

const BAND_TONE: Record<NonNullable<BrokerStats["avg_quality_band"]>, string> = {
  excepcional: "border-accent/60 text-accent",
  raro: "border-accent/50 text-accent",
  solido: "border-border text-foreground",
  regular: "border-border text-muted-foreground",
};

function memberSinceLabel(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR", { month: "short", year: "numeric" }).replace(".", "");
  } catch {
    return "—";
  }
}

export function VerifiedBrokerCard({
  brokerSlug, fallbackName, fallbackInitials, fallbackAvatar,
  onContact, onSchedule,
}: Props) {
  const fetchProfile = useServerFn(getBrokerProfile);
  const { data, isLoading } = useQuery({
    queryKey: ["broker-profile", brokerSlug],
    queryFn: () => fetchProfile({ data: { slug: brokerSlug } }),
    staleTime: 60_000,
  });

  const profile = data?.profile ?? null;
  const stats = data?.stats ?? null;
  const org = profile?.organization ?? null;
  const name = profile?.display_name ?? fallbackName;
  const firstName = name.split(" ")[0] ?? name;
  const avatar = profile?.avatar_url ?? fallbackAvatar ?? null;
  const verified = !!(profile?.creci && org);

  return (
    <div className="rounded-2xl border border-border bg-surface/40 p-6">
      <div className="mb-4 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          Corretor responsável
        </span>
        {verified && (
          <span className="inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-accent">
            <BadgeCheck className="h-3 w-3" /> verificado
          </span>
        )}
      </div>

      <div className="flex items-start gap-3">
        {avatar ? (
          <img
            src={avatar}
            alt={name}
            className="size-12 shrink-0 rounded-full border border-accent/40 object-cover"
          />
        ) : (
          <div className="grid size-12 shrink-0 place-items-center rounded-full border border-accent/40 bg-accent/10 text-sm font-medium text-accent">
            {fallbackInitials}
          </div>
        )}
        <div className="min-w-0">
          {profile ? (
            <Link
              to="/corretor/$slug"
              params={{ slug: profile.slug }}
              className="block truncate text-sm font-medium hover:text-accent"
            >
              {name}
            </Link>
          ) : (
            <div className="truncate text-sm font-medium">{name}</div>
          )}
          {profile?.headline && (
            <div className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">
              {profile.headline}
            </div>
          )}
          {profile?.creci && (
            <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground tabular-nums">
              CRECI {profile.creci_uf ? `${profile.creci_uf}-` : ""}{profile.creci}
            </div>
          )}
          {!profile && !isLoading && (
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
              especialista NOR
            </div>
          )}
        </div>
      </div>

      {/* Co-brand obrigatório */}
      {org && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-border bg-background/60 px-3 py-2">
          {org.logo_url ? (
            <img src={org.logo_url} alt={org.name} className="h-5 w-5 rounded object-cover" />
          ) : (
            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          <span className="text-[11px] text-muted-foreground">
            via <span className="text-foreground">{org.name}</span>
          </span>
        </div>
      )}

      {/* Conquistas sistêmicas */}
      {stats && (stats.active_listings > 0 || stats.cities.length > 0) && (
        <div className="mt-4 grid grid-cols-3 gap-2 border-y border-border py-3">
          <Stat
            value={String(stats.active_listings)}
            label="imóveis ativos"
            icon={<Sparkles className="h-2.5 w-2.5" />}
          />
          <Stat
            value={String(stats.cities.length || "—")}
            label="cidades"
            icon={<MapPin className="h-2.5 w-2.5" />}
          />
          <Stat
            value={memberSinceLabel(stats.member_since)}
            label="membro desde"
            icon={<Award className="h-2.5 w-2.5" />}
          />
        </div>
      )}

      {stats?.avg_quality_band && (
        <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
          <span>banda média do portfólio</span>
          <span className={`rounded-sm border px-2 py-0.5 ${BAND_TONE[stats.avg_quality_band]}`}>
            {BAND_LABEL[stats.avg_quality_band]}
          </span>
        </div>
      )}

      <div className="mt-5 grid gap-2">
        <button
          type="button"
          onClick={onContact}
          className="h-11 rounded-md bg-accent text-[11px] font-medium uppercase tracking-widest text-accent-foreground transition-opacity hover:opacity-90"
        >
          Falar com {firstName}
        </button>
        <button
          type="button"
          onClick={onSchedule}
          className="h-11 rounded-md border border-border bg-surface text-[11px] uppercase tracking-widest text-foreground transition-colors hover:border-border-strong"
        >
          Agendar visita
        </button>
      </div>

      {profile && (
        <Link
          to="/corretor/$slug"
          params={{ slug: profile.slug }}
          className="mt-3 flex items-center justify-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-accent"
        >
          ver perfil completo <ChevronRight className="h-3 w-3" />
        </Link>
      )}

    </div>
  );
}

function Stat({
  value, label, icon,
}: { value: string; label: string; icon: React.ReactNode }) {
  return (
    <div className="text-center">
      <div className="font-display text-lg leading-none tabular-nums">{value}</div>
      <div className="mt-1 flex items-center justify-center gap-1 text-[9px] uppercase tracking-widest text-muted-foreground">
        {icon} {label}
      </div>
    </div>
  );
}
